import React from 'react';
import {
    faExclamationTriangle,
    faInfoCircle,
    faToolbox,
} from '@fortawesome/free-solid-svg-icons';
import { FormGroup, Input, Alert } from 'reactstrap';
import { BaseFormViewModel } from '../common/ViewModel';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    FormLabel,
    onFieldChange,
    onReactSelectChanged,
    toasty,
    ValidationErrorMessage,
    FormCheckbox,
} from '../common/forms/FormElements';
import { Equipment } from './Equipment';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import SlideForm from '../common/forms/SlideForm';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EquipmentLocationHistoryButton from './EquipmentLocationHistoryButton';

export class EquipmentForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            equipment: this.props.equipment ?? new Equipment(),
            equipmentTypes: [],
            dispatchLocations: [],
            equipmentStatuses: [],
            employeesAssignable: [],
            equipmentAssignable: [],
            equipmentAssignableTypes: [],
            showEmployeeAssignment: true,
            showEquipmentAssignment: true,
            crews: [],
            ...new BaseFormViewModel(),
            locationDataPresent: false,
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.onUseEquipmentTypeLowboyMoveValueChanged =
            this.onUseEquipmentTypeLowboyMoveValueChanged.bind(this);
    }

    componentDidMount() {
        const { show } = this.props;

        if (show) {
            this.open();
        }
    }

    onChange = onFieldChange;

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onDelete = async (e) => {
        const { equipment } = this.state;
        const response = await util.fetch.delete(
            ApiRoutes.equipment.delete(equipment.id)
        );
        if (response) {
            this.toggleShow(false);
            this.onClose(response);
        }
    };

    onDispatchLocationChanged = async (selectedItem) => {
        const { equipment, equipmentAssignableTypes } = { ...this.state };
        let employeesAssignable = [];
        let equipmentAssignable = [];

        equipment.dispatchLocationId =
            selectedItem == null ? '' : (selectedItem.value ?? '');

        if (equipment.dispatchLocationId > 0 && equipment.equipmentTypeId > 0) {
            const [dispatchEmployees] = await Promise.all([
                util.fetch.js(
                    ApiRoutes.employees.byDispatchLocationAndEquipmentType(
                        equipment.dispatchLocationId,
                        equipment.equipmentTypeId
                    )
                ),
            ]);

            // Only allow assignment to assignable types as retrieved from equipment type dropdown cascade
            const dispatchEquipment = [
                ...equipmentAssignableTypes.filter(
                    (x) => x.dispatchLocationId === equipment.dispatchLocationId
                ),
            ];

            employeesAssignable = dispatchEmployees ?? [];
            equipmentAssignable = dispatchEquipment ?? [];
        }

        this.setState({
            equipment,
            employeesAssignable,
            equipmentAssignable,
        });
    };

    onEquipmentTypeChanged = async (selectedItem) => {
        const eq = this.state.equipment;
        const dispatchLocationId = eq.dispatchLocationId;
        let employeeAssignment = false;
        let equipmentAssignment = false;
        let employeesAssignable = [];
        let equipmentAssignableTypes = [];
        const hasGroups = !!((selectedItem ?? {}).groups ?? []).length;
        const hasRequiredEquipment =
            !!((selectedItem ?? {}).requiredEquipment ?? []).length > 0;

        // Check max assigned

        employeeAssignment = !!hasGroups && !hasRequiredEquipment;
        equipmentAssignment = !hasGroups && !!hasRequiredEquipment;

        if (hasGroups) {
            let data = await util.fetch.post(
                ApiRoutes.employees.byGroups(),
                selectedItem.groups
            );
            employeesAssignable = data ?? [];
        } else if (hasRequiredEquipment) {
            const type = selectedItem.requiredEquipment.map((e) => e.id)[0];
            const data = await util.fetch.js(ApiRoutes.equipment.byType(type));
            equipmentAssignableTypes = data ?? [];
        }

        eq.equipmentTypeId =
            selectedItem == null ? '' : (selectedItem.id ?? '');
        eq.dispatchLocationId = null; // reset the dispatch location, to reset cascade options/assignable equipment.

        this.setState(
            {
                equipment: eq,
                showEmployeeAssignment: employeeAssignment,
                showEquipmentAssignment: equipmentAssignment,
                employeesAssignable,
                equipmentAssignableTypes,
                equipmentAssignable: [], // reset assignable equipment awaiting dispatch location selection
            },
            async () => {
                const dispatchLoc = this.state.dispatchLocations.find(
                    (x) => x.value === dispatchLocationId
                );

                await this.onDispatchLocationChanged({ ...dispatchLoc });
            }
        );
    };

    onSelectChange = onReactSelectChanged;

    onSubmit = async (e) => {
        const { equipment } = this.state;
        this.setState((state) => ({ errors: {}, saving: true }));

        // Is this POST or PUT?
        const url = equipment.id
            ? ApiRoutes.equipment.update(equipment.id)
            : ApiRoutes.equipment.create();
        const fetch_cte = equipment.id ? util.fetch.put : util.fetch.post;

        const response = await fetch_cte(url, equipment).catch(
            this.handleSaveError
        );

        if (response) {
            this.onClose();
            toasty.success(
                'Equipment Saved',
                `Equipment [${equipment.description}] saved successfully.`
            );
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (equipmentId) => {
        this.setState({ loading: true });

        await this.populateState();

        let equipment = null,
            locationDataPresent = false;

        this.resetForm();

        if (equipmentId) {
            [equipment, locationDataPresent] = await Promise.all([
                util.fetch.js(ApiRoutes.equipment.byId(equipmentId)),
                util.fetch.post(
                    ApiRoutes.equipmentLocation.anyFor(equipmentId),
                    { equipmentId }
                ),
            ]);
        } else {
            equipment = new Equipment();
        }

        this.setState({ equipment, locationDataPresent }, async () => {
            this.props.toggleShow(true);
            this.context.setFormOpened(true);

            if (equipmentId) {
                // step through cascaded options on the way in
                const { equipmentTypes } = { ...this.state };
                const eqType = equipmentTypes.find(
                    (x) => x.id === equipment.equipmentTypeId
                );

                await this.onEquipmentTypeChanged({ ...eqType });
            }

            this.setState({ loading: false });
        });
    };

    async populateState() {
        const [equipmentTypes, dispatchLocations, equipmentStatuses, crews] =
            await Promise.all([
                util.fetch.js(ApiRoutes.typeAheads.equipmentTypes()),
                util.fetch.js(ApiRoutes.typeAheads.dispatchCompanyIds()),
                util.fetch.js(ApiRoutes.typeAheads.equipmentTypeStatuses()),
                util.fetch.js(ApiRoutes.crew.all()),
            ]);

        this.setState({
            equipmentTypes,
            dispatchLocations,
            equipmentStatuses,
            crews,
        });
    }

    resetForm = () => {
        this.setState({
            showEmployeeAssignment: false,
            showEquipmentAssignment: false,
            formValidated: false,
        });
    };

    getEquipmentGroupName = () => {
        const { equipment } = this.state;

        const { equipmentGroups } = this.props;

        if (!equipment || !equipmentGroups) {
            return null;
        }

        const equipmentGroup = equipmentGroups.find((eg) =>
            eg.equipmentTypeIds.includes(equipment.equipmentTypeId)
        );

        return equipmentGroup?.name;
    };

    getCrewName = () => {
        const { equipment, crews } = this.state;

        if (!equipment || !equipment.id) {
            return null;
        }

        const crewMemberships = crews.filter((c) =>
            equipment.crewIds.includes(c.id)
        );

        return crewMemberships && crewMemberships.length
            ? crewMemberships.map((c) => c.name).join(', ')
            : 'None';
    };

    getEquipmentType = () => {
        const { equipment, equipmentTypes } = this.state;

        return equipmentTypes.find((et) => et.id == equipment.equipmentTypeId);
    };

    equipmentTypeRequiresMoveBreakdown = () => {
        const { equipment } = this.state;

        const equipmentType = this.getEquipmentType() || {};
        const moveOrNot = equipment.requiresLowboyMove
            ? 'set to'
            : 'set to not ';

        let breakdown = !equipment.useEquipmentTypeLowboyMoveValue
            ? `This piece of equipment has an override in place, and is ${moveOrNot} require a lowboy for moves. `
            : '';

        const equipmentTypeMove = equipmentType.requiresLowboyMove
            ? 'set to'
            : 'set to not';

        return `${breakdown}${equipmentType.description} are ${equipmentTypeMove} require a lowboy for moves.`;
    };

    onUseEquipmentTypeLowboyMoveValueChanged = (e) => {
        const { equipment } = this.state;

        const checked = Boolean(e.target.checked);

        equipment.useEquipmentTypeLowboyMoveValue = checked;
        equipment.requiresLowboyMove = checked ? null : false;

        this.setState({ equipment });
    };

    render() {
        const {
            equipmentTypes,
            dispatchLocations,
            formValidated,
            equipmentStatuses,
            validationMessage,
            employeesAssignable,
            equipmentAssignable,
            loading,
            equipment,
            errors,
            showEmployeeAssignment,
            showEquipmentAssignment,
        } = this.state;

        if (loading) {
            return null;
        }

        const { show } = this.props;

        const equipmentGroupName = this.getEquipmentGroupName();

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="equipmentForm"
                formIcon={faToolbox}
                formTitle={
                    !!parseInt(equipment.id ?? 0) > 0
                        ? 'Edit Equipment'
                        : 'Add Equipment'
                }
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={errors}
                onClearErrors={this.onClearErrors}
                validationMessage={validationMessage}
            >
                <FormGroup>
                    <FormLabel
                        htmlFor="equipmentTypeId"
                        text="Equipment Type"
                        required
                    />
                    <ValidatedSelect
                        id="equipmentTypeId"
                        name="equipmentTypeId"
                        required
                        options={equipmentTypes}
                        value={
                            equipmentTypes.find(
                                (i) => i.id === equipment.equipmentTypeId
                            ) ?? ''
                        }
                        getOptionLabel={(option) => option.description}
                        getOptionValue={(option) => option.id}
                        onChange={this.onEquipmentTypeChanged}
                        validationMessage="The equipment type is required."
                    />
                </FormGroup>

                {equipmentGroupName && (
                    <Alert color="info" fade={false}>
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                        This will be in the {equipmentGroupName} equipment
                        group.
                    </Alert>
                )}

                {equipment.equipmentTypeId && !equipmentGroupName && (
                    <Alert color="warning" fade={false}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="mr-2"
                        />
                        This equipment type is not currently associated with an
                        equipment group.
                    </Alert>
                )}

                <FormGroup>
                    <FormLabel htmlFor="identifier" text="Number" required />
                    <Input
                        id="identifier"
                        name="equipment.identifier"
                        value={equipment.identifier ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter number"
                        type="text"
                        required
                    />
                    <ValidationErrorMessage>
                        Number is required.
                    </ValidationErrorMessage>
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        htmlFor="description"
                        text="Description"
                        required
                    />
                    <Input
                        id="description"
                        name="equipment.description"
                        value={equipment.description ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter description"
                        type="text"
                        required
                    />
                    <ValidationErrorMessage>
                        Description is required.
                    </ValidationErrorMessage>
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        htmlFor="dispatchLocationId"
                        text="Dispatching"
                        required
                    />
                    <ValidatedSelect
                        id="dispatchLocationId"
                        name="equipment.dispatchLocationId"
                        required
                        options={dispatchLocations}
                        value={
                            dispatchLocations.find(
                                (i) => i.value === equipment.dispatchLocationId
                            ) ?? ''
                        }
                        onChange={this.onDispatchLocationChanged}
                        validationMessage="Dispatching Office is required."
                    />
                </FormGroup>

                <FormGroup>
                    <FormLabel
                        htmlFor="equipmentStatusId"
                        text="Status"
                        required
                    />
                    <ValidatedSelect
                        id="equipmentStatusId"
                        name="equipmentStatusId"
                        required
                        options={equipmentStatuses}
                        value={
                            equipmentStatuses.find(
                                (i) => i.value === equipment.equipmentStatusId
                            ) ?? ''
                        }
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        onChange={(sel) => {
                            const value = (sel ?? {}).value ?? '';
                            this.setState(
                                (state) => (
                                    (state.equipment.equipmentStatusId = value),
                                    state
                                )
                            );
                        }}
                        validationMessage="Status is required."
                    />
                </FormGroup>

                {equipment.equipmentTypeId && (
                    <>
                        <Alert color="info" fade={false}>
                            <div className="float-left mr-2">
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </div>
                            <div>
                                {this.equipmentTypeRequiresMoveBreakdown()}
                            </div>
                        </Alert>

                        <FormGroup>
                            <FormCheckbox
                                name="equipment.useEquipmentTypeLowboyMoveValue"
                                checked={
                                    equipment.useEquipmentTypeLowboyMoveValue
                                }
                                onChange={
                                    this
                                        .onUseEquipmentTypeLowboyMoveValueChanged
                                }
                                labelText="Requires Lowboy Move - Use Equipment Type's Value"
                                id="equipment.useEquipmentTypeLowboyMoveValue"
                            />
                        </FormGroup>
                    </>
                )}

                {!equipment.useEquipmentTypeLowboyMoveValue && (
                    <FormGroup>
                        <FormCheckbox
                            name="equipment.requiresLowboyMove"
                            checked={equipment.requiresLowboyMove}
                            onChange={this.onChange}
                            labelText="Requires Lowboy Move"
                            id="equipment.requiresLowboyMove"
                        />
                    </FormGroup>
                )}

                <FormGroup>
                    <FormLabel text="Crew Assignment" />
                    <input
                        type="text"
                        className="form-control"
                        disabled={true}
                        readOnly={true}
                        value={this.getCrewName()}
                    />
                </FormGroup>

                {(showEmployeeAssignment || !!equipment.employeeAssignedId) && (
                    <FormGroup>
                        <FormLabel
                            htmlFor="employeeAssignmentId"
                            text="Employee Assigned"
                        />
                        <ValidatedSelect
                            id="employeeAssignmentId"
                            name="employeeAssignmentId"
                            isClearable={true}
                            options={employeesAssignable}
                            value={
                                employeesAssignable.find(
                                    (i) => i.id === equipment.employeeAssignedId
                                ) ?? ''
                            }
                            getOptionLabel={(option) => option.fullName}
                            getOptionValue={(option) => option.id}
                            onChange={(sel) => {
                                const value = (sel ?? {}).id ?? '';
                                this.setState(
                                    (state) => (
                                        (state.equipment.employeeAssignedId =
                                            value),
                                        state
                                    )
                                );
                            }}
                        />
                    </FormGroup>
                )}

                {showEquipmentAssignment && (
                    <FormGroup>
                        <FormLabel
                            htmlFor="equipmentAssignmentId"
                            text="Equipment Assigned"
                        />
                        <ValidatedSelect
                            id="equipmentAssignmentId"
                            name="equipmentAssignmentId"
                            isDisabled={!(equipment ?? {}).dispatchLocationId}
                            options={equipmentAssignable}
                            value={
                                equipmentAssignable.find(
                                    (i) =>
                                        i.id === equipment.equipmentAssignedId
                                ) ?? ''
                            }
                            getOptionLabel={(option) =>
                                `${option.description} (${option.identifier})`
                            }
                            getOptionValue={(option) => option.id}
                            onChange={(sel) => {
                                const value = (sel ?? {}).id ?? '';
                                this.setState(
                                    (state) => (
                                        (state.equipment.equipmentAssignedId =
                                            value),
                                        state
                                    )
                                );
                            }}
                        />
                    </FormGroup>
                )}

                <EquipmentLocationHistoryButton {...this.state} />
            </SlideForm>
        );
    }
}
